/**
 * Building blocks of the batch sheets and dialogs (W2-11 batch-tools; DESIGN.md §4.14, §5.8):
 *  - `ToolSheet`: the non-modal side sheet frame with the live status line, Cancel and Apply. Enter applies (after
 *    the focused field commits its text; not on buttons, lists and invalid fields), Ctrl+Enter applies from
 *    anywhere in the sheet, Escape (Sheet) cancels.
 *  - `CurveField`: an easing chooser (all 29 RPE curves with their plain names, thumbnails, "More curves..." opens
 *    the easing picker for parts of curves and custom curves).
 *  - `NumberInput` / `BeatInput`: NumberField and BeatField for plain values.
 *  - `ExpressionBox`: a tool's equivalent expression or formula (D§5.8), optionally with Copy.
 *  - `useText`: translation that follows the language.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent as ReactFocusEvent,
  type SyntheticEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { DIALOG } from '../../app/dialogs/types';
import type { AppServices } from '../../app/registry/types';
import { EASING_CATALOG, LINEAR } from '../../core/ease/index';
import type { Ease, EaseUse } from '../../core/ease/types';
import type { Beat, BeatParseContext } from '../../core/time/types';
import type { MessageRef } from '../../core/util/types';
import { useT } from '../../i18n/useT';
import { BeatField } from '../../ui/BeatField';
import { Button } from '../../ui/Button';
import { CurveThumb } from '../../ui/CurveThumb';
import { Icon } from '../../ui/Icon';
import { NumberField } from '../../ui/NumberField';
import { Select } from '../../ui/Select';
import { Sheet } from '../../ui/Sheet';
import { m } from './batch.messages';
import s from './Batch.module.css';
import { snapDiv, tempoOf } from './batchContext';
import { registerSheet } from './sheetRegistry';

// ---------------------------------------------------------------------------------------------------------------
// Text
// ---------------------------------------------------------------------------------------------------------------

export interface Text {
  t(key: string, params?: Readonly<Record<string, string | number>>): string;
  tr(r: MessageRef): string;
}

export function useText(): Text {
  const t = useT();
  return useMemo(() => ({ t, tr: (r: MessageRef) => t(r.key, r.params) }), [t]);
}

// ---------------------------------------------------------------------------------------------------------------
// Sheet frame
// ---------------------------------------------------------------------------------------------------------------

export type SheetStatus =
  | { readonly kind: 'ok'; readonly text: string }
  | { readonly kind: 'warn'; readonly text: string }
  | { readonly kind: 'error'; readonly text: string }
  | { readonly kind: 'none' };

export interface ToolSheetProps {
  readonly title: string;
  readonly subtitle?: string | undefined;
  readonly status: SheetStatus;
  readonly applyLabel: string;
  /** Why Apply is unavailable (null = available). */
  readonly applyDisabledReason?: MessageRef | null | undefined;
  readonly onApply: () => void;
  readonly onCancel: () => void;
  readonly children: ReactNode;
}

/** Elements on which Enter keeps its own meaning (activate, open, pick). */
const ENTER_OWNERS =
  'button:not([role="checkbox"]):not([role="radio"]):not([role="switch"]), a[href], select, textarea, [role="button"], [role="menuitem"], [role="tab"], [role="option"], [role="combobox"], [role="listbox"], [aria-expanded="true"]';

export function StatusLine(props: { readonly status: SheetStatus }) {
  const st = props.status;
  if (st.kind === 'none') return <p className={s.status} role="status" aria-live="polite" />;
  const cls = st.kind === 'error' ? s.statusError : st.kind === 'warn' ? s.statusWarn : '';
  const icon = st.kind === 'error' ? 'CircleAlert' : st.kind === 'warn' ? 'CirclePause' : 'Check';
  return (
    <p className={`${s.status} ${cls}`} role="status" aria-live="polite">
      <Icon name={icon} size={14} />
      <span>{st.text}</span>
    </p>
  );
}

/** Text-entry elements: Enter there first commits what was typed. */
const TEXT_ENTRY = 'input:not([type="checkbox"]):not([type="radio"]), textarea, [contenteditable="true"]';

export interface ApplyOnEnter {
  readonly onKeyDown: (e: ReactKeyboardEvent<HTMLElement>) => void;
  readonly onInput: (e: SyntheticEvent<HTMLElement>) => void;
  readonly onBlur: (e: ReactFocusEvent<HTMLElement>) => void;
}

/**
 * Enter applies; Ctrl+Enter applies from anywhere. In a text field, the Enter that ends typing only commits the
 * field (so the preview can be checked first); the next Enter there applies. Enter keeps its own meaning on
 * buttons, lists and menus, and never applies from a field showing an error. Spread the handlers on the form element.
 */
export function useApplyOnEnter(onApply: () => void, disabled: boolean): ApplyOnEnter {
  const applyRef = useRef(onApply);
  useEffect(() => {
    applyRef.current = onApply;
  });
  const [dirty] = useState(() => new WeakSet<EventTarget>());
  const onInput = useCallback(
    (e: SyntheticEvent<HTMLElement>) => {
      dirty.add(e.target);
    },
    [dirty],
  );
  const onBlur = useCallback(
    (e: ReactFocusEvent<HTMLElement>) => {
      dirty.delete(e.target);
    },
    [dirty],
  );
  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLElement>) => {
      if (e.key !== 'Enter' || e.nativeEvent.isComposing || e.altKey || e.shiftKey) return;
      const target = e.target instanceof Element ? e.target : null;
      const ctrl = e.ctrlKey || e.metaKey;
      if (!ctrl && target !== null) {
        if (target.closest(ENTER_OWNERS) !== null) return;
        if (target.matches(TEXT_ENTRY) && dirty.has(target)) {
          // This Enter commits the typed text (the field's own handler); the next one applies.
          dirty.delete(target);
          return;
        }
      }
      e.preventDefault();
      if (disabled) return;
      if (ctrl && target instanceof HTMLElement) target.blur();
      // Let the focused field finish its own Enter / blur handling first, then apply.
      setTimeout(() => {
        const active = document.activeElement;
        if (!ctrl && active?.getAttribute('aria-invalid') === 'true') return;
        applyRef.current();
      }, 0);
    },
    [disabled, dirty],
  );
  return { onKeyDown, onInput, onBlur };
}

export function ToolSheet(props: ToolSheetProps) {
  const text = useText();
  const disabled = props.applyDisabledReason !== undefined && props.applyDisabledReason !== null;
  const enter = useApplyOnEnter(props.onApply, disabled);
  // One sheet at a time and Escape from anywhere (sheetRegistry.ts, UX-038): register the latest Cancel.
  const cancelRef = useRef(props.onCancel);
  useEffect(() => {
    cancelRef.current = props.onCancel;
  });
  useEffect(
    () =>
      registerSheet(() => {
        cancelRef.current();
      }),
    [],
  );
  return (
    <Sheet
      open
      title={props.title}
      onClose={props.onCancel}
      footer={
        <div className={s.footer}>
          <div className={s.footerStatus}>
            <StatusLine status={props.status} />
          </div>
          <Button variant="ghost" onClick={props.onCancel}>
            {text.t(m.cancel)}
          </Button>
          <Button variant="primary" onClick={props.onApply} disabledReason={props.applyDisabledReason ?? null}>
            {props.applyLabel}
          </Button>
        </div>
      }
    >
      <div className={s.form} {...enter} data-batch-sheet="">
        {props.subtitle !== undefined && <p className={s.subtitle}>{props.subtitle}</p>}
        {props.children}
      </div>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Fields
// ---------------------------------------------------------------------------------------------------------------

export function NumberInput(props: {
  readonly label: string;
  readonly value: number;
  readonly onChange: (v: number) => void;
  readonly min?: number | undefined;
  readonly max?: number | undefined;
  readonly step?: number | undefined;
  readonly unit?: string | undefined;
  readonly precision?: number | undefined;
  readonly help?: MessageRef | undefined;
  readonly integer?: boolean | undefined;
  readonly disabled?: boolean | undefined;
  /** Label beside the field instead of above it (default: above, the one layout of the 360 px sheets, UX-046). */
  readonly inline?: boolean | undefined;
}) {
  const clamp = (v: number): number => {
    let x = props.integer === true ? Math.round(v) : v;
    if (props.min !== undefined) x = Math.max(props.min, x);
    if (props.max !== undefined) x = Math.min(props.max, x);
    return x === 0 ? 0 : x;
  };
  return (
    <NumberField
      label={props.label}
      value={{ kind: 'value', value: props.value }}
      onCommit={(v) => {
        if (v.kind === 'number' && Number.isFinite(v.value)) props.onChange(clamp(v.value));
      }}
      onScrub={(v) => {
        if (Number.isFinite(v)) props.onChange(clamp(v));
      }}
      vars="field"
      stacked={props.inline !== true}
      {...(props.min === undefined ? {} : { min: props.min })}
      {...(props.max === undefined ? {} : { max: props.max })}
      {...(props.step === undefined ? {} : { step: props.step })}
      {...(props.unit === undefined ? {} : { unit: props.unit })}
      {...(props.precision === undefined ? {} : { precision: props.precision })}
      {...(props.help === undefined ? {} : { help: props.help })}
      {...(props.disabled === true ? { disabled: true } : {})}
    />
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Equivalent expression
// ---------------------------------------------------------------------------------------------------------------

/**
 * The inline form of a tool (D§5.8 "each dialog shows its equivalent expression or query"): a caption, the text in
 * a code box, an optional note under it, and Copy when the text can be pasted into a field as it is.
 */
export function ExpressionBox(props: {
  readonly services: Pick<AppServices, 'ui'>;
  readonly label: string;
  readonly code: string;
  readonly note?: string | undefined;
  readonly copyable?: boolean | undefined;
  readonly children?: ReactNode;
}) {
  const text = useText();
  const { services, code } = props;
  const copy = (): void => {
    void navigator.clipboard.writeText(code).then(
      () => {
        services.ui.toast({ key: m.expressionCopied }, { kind: 'info' });
      },
      () => {
        services.ui.toast({ key: m.copyFailed }, { kind: 'error' });
      },
    );
  };
  return (
    <div className={s.group}>
      <p className={s.groupTitle}>{props.label}</p>
      <div className={s.expression}>
        <pre className={s.code} aria-label={props.label}>
          {code}
        </pre>
        {props.copyable === true && (
          <Button variant="ghost" size="sm" icon="Copy" onClick={copy}>
            {text.t(m.copy)}
          </Button>
        )}
        {props.children}
      </div>
      {props.note !== undefined && <p className={s.hint}>{props.note}</p>}
    </div>
  );
}

/** Beat parse context of the open project (snap, syntax, tempo). */
export function useBeatContext(services: AppServices): BeatParseContext {
  const tempo = tempoOf(services);
  const syntax = services.settings.value.editing.beatSyntax;
  const div = snapDiv(services);
  return useMemo(() => ({ snapDiv: div, syntax, ...(tempo === null ? {} : { tempo }) }), [div, syntax, tempo]);
}

export function BeatInput(props: {
  readonly label: string;
  readonly value: Beat;
  readonly onChange: (b: Beat) => void;
  readonly ctx: BeatParseContext;
}) {
  return (
    <BeatField
      label={props.label}
      value={{ kind: 'value', value: props.value }}
      ctx={props.ctx}
      stacked
      echo
      onCommit={(b) => {
        props.onChange(b);
      }}
    />
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Curve chooser
// ---------------------------------------------------------------------------------------------------------------

type CurveOption = 'custom' | `${number}`;

function optionOf(e: Ease): CurveOption {
  return e.kind === 'tween' && e.left === 0 && e.right === 1 ? (String(e.id) as CurveOption) : 'custom';
}

/** Plain name of a whole RPE curve ("Gentle, slow both" in Guided; "InOutSine" in Full). */
export function curveName(text: Text, id: number, full: boolean): string {
  const entry = EASING_CATALOG[id - 1];
  if (entry === undefined) return text.t(m.curveNumber, { id });
  if (entry.strength === null || entry.shape === null) return text.t('ease.name.linear');
  if (full) return `${entry.rpeName} (${String(id)})`;
  return text.t(m.curvePlain, {
    strength: text.t(`ease.strength.${entry.strength}`),
    shape: text.t(`ease.shape.${entry.shape}`),
  });
}

/** Name of any model ease. */
export function easeLabel(text: Text, e: Ease, full: boolean): string {
  if (e.kind === 'hold') return text.t('ease.name.hold');
  if (e.kind === 'bezier') return text.t('ease.name.bezier');
  const whole = e.left === 0 && e.right === 1;
  const name = curveName(text, e.id, full);
  return whole ? name : text.t(m.curvePart, { name, left: e.left, right: e.right });
}

export function CurveField(props: {
  readonly services: AppServices;
  readonly label: string;
  readonly value: Ease;
  readonly onChange: (e: Ease) => void;
  readonly use?: EaseUse | undefined;
}) {
  const text = useText();
  const full = props.services.settings.value.general.level === 'full';
  const options = useMemo(() => {
    const list: { value: CurveOption; label: string }[] = EASING_CATALOG.map((e) => ({
      value: String(e.easingType) as CurveOption,
      label: curveName(text, e.easingType, full),
    }));
    return list;
  }, [text, full]);
  const current = optionOf(props.value);
  const shown =
    current === 'custom'
      ? [{ value: 'custom' as CurveOption, label: easeLabel(text, props.value, full) }, ...options]
      : options;
  const more = async (): Promise<void> => {
    const r = await props.services.ui.openDialog(DIALOG.easingPicker, {
      segments: [],
      initial: props.value,
      use: props.use ?? 'general',
    });
    if (r !== null) props.onChange(r);
  };
  return (
    <div className={s.curve}>
      <CurveThumb ease={props.value} size={28} showOvershoot />
      <Select<CurveOption>
        label={props.label}
        value={current}
        options={shown}
        onChange={(v) => {
          if (v === 'custom') return;
          const id = Number(v);
          props.onChange(id === 1 ? LINEAR : { kind: 'tween', id, left: 0, right: 1 });
        }}
      />
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          void more();
        }}
      >
        {text.t(m.moreCurves)}
      </Button>
    </div>
  );
}
